import captureFubUserWithConsent from 'pages/api/consent-disclaimers/consentApiClient';
import { evaluateConsent } from 'domain/consent';
import type { ConsentRuleInput } from 'domain/consent';
import type { IUserLocation } from 'utils/user-location';
import type { ThemeNames } from '@tenants/types';

export type ConsentCaptureData = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  source: ThemeNames;
  listingId?: number;
  listingUrl?: string;
};

export type ConsentValidation = { ok: true } | { ok: false; reason: 'consent_required' };

/**
 * ConsentService
 *
 * Plug-in service that answers two questions for any form:
 *   1. Is consent required? (`isRequired`)
 *   2. Has the user satisfied it? (`validate`)
 *
 * Call `capture` exactly once on successful form submit — the service decides
 * internally whether to make the API call.
 *
 * Accepts a `ConsentRule` directly (not a full tenant) so it can be
 * instantiated from any context, including an admin-app API response.
 */
export class ConsentService {
  readonly isRequired: boolean;

  constructor(
    private readonly rule: ConsentRuleInput,
    private readonly userLocation: IUserLocation | null,
  ) {
    this.isRequired = evaluateConsent(rule, userLocation?.countryCode ?? null).required;
  }

  /**
   * Validates whether the user has satisfied the consent requirement.
   * Always returns `{ ok: true }` when consent is not required for this user.
   */
  validate(hasConsented: boolean): ConsentValidation {
    if (!this.isRequired) return { ok: true };
    return hasConsented ? { ok: true } : { ok: false, reason: 'consent_required' };
  }

  /**
   * Captures consent to the API.
   * No-ops silently when consent is not required — callers never need to branch.
   */
  async capture(data: ConsentCaptureData, disclaimerText: string): Promise<void> {
    if (!this.isRequired) return;

    await captureFubUserWithConsent({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      source: data.source,
      userIp: this.userLocation?.ip ?? '',
      hasConsented: true,
      consentDisclaimerText: disclaimerText,
      countryCode: this.userLocation?.countryCode,
      createdAt: new Date().toISOString(),
      ...(data.listingId !== undefined && { listingId: data.listingId }),
      ...(data.listingUrl !== undefined && { listingUrl: data.listingUrl }),
    });
  }
}

export function createConsentService(
  rule: ConsentRuleInput,
  userLocation: IUserLocation | null,
): ConsentService {
  return new ConsentService(rule, userLocation);
}
